import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Newspaper, Plus, Trash2, Loader2, Pencil, Eye, EyeOff } from "lucide-react";
import {
  adminListNews,
  adminUpsertNews,
  adminDeleteNews,
} from "@/lib/api/news.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/portal/admin/news")({
  head: () => ({
    meta: [{ title: "Admin · News" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminNewsPage,
});

const CATEGORIES = [
  "legal",
  "labour",
  "immigration",
  "compliance",
  "consumer",
  "education",
  "general",
];

function AdminNewsPage() {
  const qc = useQueryClient();
  const fetchAll = useServerFn(adminListNews);
  const upsert = useServerFn(adminUpsertNews);
  const remove = useServerFn(adminDeleteNews);

  const { data } = useQuery({
    queryKey: ["admin-news"],
    queryFn: () => fetchAll(),
  });

  const [editing, setEditing] = useState<any | null>(null);
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [sourceName, setSourceName] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [cover, setCover] = useState("");
  const [published, setPublished] = useState(true);
  const [saving, setSaving] = useState(false);

  function startEdit(p: any) {
    setEditing(p);
    setTitle(p.title);
    setExcerpt(p.excerpt ?? "");
    setBody(p.body ?? "");
    setCategory(p.category ?? "general");
    setSourceName(p.source_name ?? "");
    setSourceUrl(p.source_url ?? "");
    setCover(p.cover_image_url ?? "");
    setPublished(!!p.is_published);
  }
  function reset() {
    setEditing(null);
    setTitle("");
    setExcerpt("");
    setBody("");
    setCategory("general");
    setSourceName("");
    setSourceUrl("");
    setCover("");
    setPublished(true);
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await upsert({
        data: {
          ...(editing?.id ? { id: editing.id } : {}),
          title: title.trim(),
          excerpt: excerpt.trim(),
          body: body.trim(),
          category: category as any,
          source_name: sourceName.trim(),
          source_url: sourceUrl.trim(),
          cover_image_url: cover.trim(),
          is_published: published,
        },
      });
      toast.success(editing ? "Post updated" : "Post created");
      reset();
      qc.invalidateQueries({ queryKey: ["admin-news"] });
      qc.invalidateQueries({ queryKey: ["public-news"] });
    } catch (err: any) {
      toast.error("Save failed", { description: err?.message });
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string) {
    if (!confirm("Delete this post?")) return;
    try {
      await remove({ data: { id } });
      toast.success("Deleted");
      if (editing?.id === id) reset();
      qc.invalidateQueries({ queryKey: ["admin-news"] });
      qc.invalidateQueries({ queryKey: ["public-news"] });
    } catch (err: any) {
      toast.error("Delete failed", { description: err?.message });
    }
  }

  const posts = data?.posts ?? [];

  return (
    <div className="space-y-8">
      <header className="flex items-start gap-3">
        <Newspaper className="mt-1 h-5 w-5 text-gold" />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
          <h2 className="font-display text-2xl text-navy">News &amp; updates</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Publish current happenings — labour, immigration, compliance, consumer, etc.
          </p>
        </div>
      </header>

      <form
        onSubmit={onSave}
        className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4"
      >
        <h3 className="font-display text-lg text-navy flex items-center gap-2">
          {editing ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {editing ? `Edit: ${editing.title}` : "Create new post"}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Title</Label>
            <Input
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cover image URL (optional)</Label>
            <Input
              value={cover}
              onChange={(e) => setCover(e.target.value)}
              placeholder="https://…"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Source name</Label>
            <Input
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              placeholder="e.g. Dept of Home Affairs"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Source URL</Label>
            <Input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://…"
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Excerpt (shown on cards)</Label>
            <Textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              maxLength={500}
              className="mt-1 min-h-[60px]"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Body</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={20000}
              className="mt-1 min-h-[160px]"
              placeholder="Full article text. Plain text — paragraphs preserved."
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
              className="h-4 w-4 accent-navy"
            />
            Published
          </label>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={saving} className="bg-navy hover:bg-navy/90">
            {saving ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
            ) : editing ? "Update post" : "Publish post"}
          </Button>
          {editing && (
            <Button type="button" variant="outline" onClick={reset}>
              Cancel edit
            </Button>
          )}
        </div>
      </form>

      <section>
        <h3 className="font-display text-lg text-navy">All posts</h3>
        {posts.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No posts yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border rounded-xl border border-border bg-card">
            {posts.map((p: any) => (
              <li
                key={p.id}
                className="flex flex-wrap items-start gap-3 p-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-widest text-navy">
                      {p.category}
                    </span>
                    {p.is_published ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                        <Eye className="h-3 w-3" /> live
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <EyeOff className="h-3 w-3" /> draft
                      </span>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(p.published_at).toLocaleDateString("en-ZA")}
                    </span>
                  </div>
                  <p className="mt-1 font-medium text-navy">{p.title}</p>
                  {p.excerpt && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{p.excerpt}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => startEdit(p)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(p.id)}
                    className="text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
